import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenantSafe } from '../context/TenantContext';
import { getTenants } from '../services/tenantService';
import { getMyRecords, deleteRecord } from '../services/harvestingService';
import HarvestRecordEditor from '../components/harvesting/HarvestRecordEditor';
import './AccountPage.css';

const tenantName = (tenant) => tenant.branding?.displayName || tenant.name;
const displayDate = (value) => {
  if (!value) return 'Not provided';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not provided' : date.toLocaleDateString(undefined, { timeZone: 'UTC' });
};
const displayValue = (value) => Array.isArray(value) ? value.join(', ') : String(value);
const detailFields = [
  ['activityType', 'Activity'], ['resourceOutcome', 'Outcome'], ['uses', 'Uses'],
  ['otherUse', 'Other use'], ['condition', 'Condition'], ['sex', 'Sex'],
  ['ageOrSizeClass', 'Age or size class'], ['caughtQuantity', 'Caught quantity'],
  ['sizeValue', 'Size'], ['sizeUnit', 'Size unit'], ['observedSigns', 'Observed signs'],
  ['unusualConditions', 'Unusual conditions'],
];

function DeleteHarvestRecord({ tenantId, record, onDeleted }) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const remove = async () => {
    setDeleting(true);
    setError('');
    try {
      await deleteRecord(tenantId, record._id);
      onDeleted();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to delete this harvest record. Please retry.');
      setDeleting(false);
    }
  };
  return <div className="my-data-delete">
    {!confirming ? <button type="button" onClick={() => setConfirming(true)}>Delete harvest record</button> : <>
      <p>Permanently delete this {record.species || 'harvest'} record? This cannot be undone.</p>
      {error && <p role="alert">{error}</p>}
      <button type="button" disabled={deleting} onClick={remove}>{deleting ? 'Deleting...' : 'Confirm delete'}</button>
      <button type="button" disabled={deleting} onClick={() => { setConfirming(false); setError(''); }}>Keep record</button>
    </>}
  </div>;
}

function MyHarvestData({ tenant, recordId }) {
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');
  useEffect(() => {
    let current = true;
    setResult(null);
    setError('');
    getMyRecords(tenant._id, { page, limit: 20, ...(recordId ? { recordId } : {}) }).then(({ data }) => {
      if (!Array.isArray(data?.records) || !Number.isInteger(data.total)
        || !Number.isInteger(data.pages) || data.page !== page) {
        throw new Error('Unable to read your harvest records. Please retry.');
      }
      if (current) setResult({ ...data, requestedPage: page });
    }).catch((err) => {
      if (current) setError(err.response?.data?.message || err.message || 'Unable to load your harvest records. Please retry.');
    });
    return () => { current = false; };
  }, [tenant._id, page, revision, recordId]);
  const visible = result?.requestedPage === page ? result : null;
  return (
    <section aria-label="My harvesting data">
      <h2>My harvesting data</h2>
      {message && <p role="status">{message}</p>}
      {recordId && <a href={tenant.slug ? `/t/${tenant.slug}/my-data` : `/my-data?${new URLSearchParams({ tenantId: tenant._id })}`}>All my harvest records</a>}
      <p>Only your harvest records in {tenantName(tenant)} are shown here, including records entered for you by staff.
        Other communities and other people's records are not included.</p>
      {error ? <div role="alert"><p>{error}</p>
        <button onClick={() => setRevision((value) => value + 1)}>Retry harvest records</button>
      </div> : !visible ? <p role="status">Loading your harvest records...</p> : <>
        <p role="status">{visible.total} harvest {visible.total === 1 ? 'record' : 'records'} in {tenantName(tenant)}</p>
        {visible.total === 0 ? <p>{recordId
          ? 'This harvest record was not found in your data for this community.'
          : 'You have no harvest records in this community yet.'}</p> : (
          <ul className="my-data-records">
            {visible.records.map((record) => (
              <li key={record._id}>
                <details className="my-data-record" open={recordId === record._id ? true : undefined}>
                  <summary>
                    <span className="my-data-record__species">{record.speciesId?.commonName || record.species || record.category}</span>
                    <span>{displayDate(record.harvestDate)}</span>
                    <span>{record.quantity == null ? 'Quantity not provided' : `${record.quantity} ${record.unit || ''}`.trim()}</span>
                    <span className="my-data-record__more">More details</span>
                  </summary>
                  <dl className="my-data-record__details">
                    <dt>Category</dt><dd>{record.category}{record.subcategory ? ` / ${record.subcategory}` : ''}</dd>
                    <dt>Status</dt><dd>{({ approved: 'Recorded', submitted: 'Submitted', draft: 'Draft', rejected: 'Not accepted' })[record.status] || 'Not provided'}</dd>
                    {record.season && <><dt>Season</dt><dd>{record.season}</dd></>}
                    {record.locationDescription && <><dt>Location</dt><dd>{record.locationDescription}</dd></>}
                    {record.location?.coordinates?.length === 2 && <><dt>Coordinates (longitude, latitude)</dt><dd>{record.location.coordinates.join(', ')}</dd></>}
                    {record.locationPrecision && <><dt>Location accuracy</dt><dd>{record.locationPrecision === 'confirmed' ? 'Exact / confirmed' : record.locationPrecision}</dd></>}
                    {record.abundance && <><dt>Abundance</dt><dd>{record.abundance}</dd></>}
                    {record.entryChannel && <><dt>Recorded by</dt><dd>{record.entryChannel === 'staff_assisted' ? 'Staff, on your behalf' : 'You'}</dd></>}
                    {record.isTraditionalUse && <><dt>Traditional use</dt><dd>Yes</dd></>}
                    {record.observations && <><dt>Observations</dt><dd className="my-data-notes">{record.observations}</dd></>}
                    {record.traditionalKnowledge && <><dt>Knowledge shared</dt><dd className="my-data-notes">{record.traditionalKnowledge}</dd></>}
                    {!!record.mediaUrls?.length && <><dt>Attachment URLs</dt><dd className="my-data-notes">{record.mediaUrls.join('\n')}</dd></>}
                    {detailFields.map(([key, label]) => {
                      const value = record.harvestDetails?.[key];
                      return value == null || value === '' || (Array.isArray(value) && !value.length) ? null
                        : <React.Fragment key={key}><dt>{label}</dt><dd>{displayValue(value)}</dd></React.Fragment>;
                    })}
                    {record.harvestDetails?.reproductiveSignsObserved && <><dt>Reproductive signs observed</dt><dd>Yes</dd></>}
                    {record.harvestDetails?.followUpRequested && <><dt>Follow-up requested</dt><dd>Yes</dd></>}
                  </dl>
                  {editingId === record._id ? <HarvestRecordEditor tenantId={tenant._id} record={record}
                    onCancel={() => setEditingId(null)} onSaved={() => {
                      setEditingId(null);
                      setMessage('Harvest record updated.');
                      setRevision((value) => value + 1);
                    }} /> : <>
                    <button type="button" onClick={() => { setEditingId(record._id); setMessage(''); }}>Edit harvest record</button>
                    <DeleteHarvestRecord tenantId={tenant._id} record={record} onDeleted={() => {
                      setMessage('Harvest record deleted.');
                      if (visible.records.length === 1 && page > 1) setPage((value) => value - 1);
                      else setRevision((value) => value + 1);
                    }} />
                  </>}
                </details>
              </li>
            ))}
          </ul>
        )}
        {visible.pages > 1 && <nav aria-label="Harvest record pages" className="my-data-pagination">
          <button disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</button>
          <span>Page {page} of {visible.pages}</span>
          <button disabled={page >= visible.pages} onClick={() => setPage((value) => value + 1)}>Next</button>
        </nav>}
      </>}
    </section>
  );
}

export default function MyDataPage() {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const recordId = query.get('recordId') || '';
  const requestedTenantId = query.get('tenantId') || '';
  const tenantCtx = useTenantSafe();
  const hasTenantContext = !!tenantCtx;
  const { memberships = [], activeTenant, publicTenant } = useAuth();
  const [publicTenants, setPublicTenants] = useState([]);
  const [loading, setLoading] = useState(!tenantCtx);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [selectedId, setSelectedId] = useState(requestedTenantId);
  useEffect(() => {
    if (hasTenantContext) return undefined;
    let current = true;
    setLoading(true);
    setError('');
    getTenants().then(({ data }) => {
      if (!Array.isArray(data)) throw new Error('Unable to read the community list.');
      if (current) setPublicTenants(data.filter((tenant) => tenant.type === 'public' || ['public', 'general-public'].includes(tenant.slug)));
    }).catch((err) => {
      if (current) setError(err.response?.data?.message || err.message || 'Unable to load communities. Please retry.');
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [hasTenantContext, revision]);

  const choices = [...new Map([
    ...memberships.map((membership) => ({
      _id: typeof membership.tenantId === 'object' ? membership.tenantId._id : membership.tenantId,
      slug: membership.tenantSlug || membership.tenantId?.slug,
      name: membership.tenantName || membership.tenantId?.name,
      branding: membership.branding || membership.tenantId?.branding,
    })), activeTenant, publicTenant, ...publicTenants,
  ].filter((tenant) => tenant?._id).map((tenant) => [String(tenant._id), tenant])).values()];
  const defaultId = activeTenant?._id || publicTenant?._id || (!loading && choices.length === 1 ? choices[0]._id : '');
  const selected = choices.find((tenant) => String(tenant._id) === (selectedId || String(defaultId)));
  const tenant = tenantCtx ? tenantCtx.viewTenant : selected;
  return (
    <div className="page account-page">
      <h1>My data</h1>
      <p>View the information you have shared with each community. Starting with your harvesting data.</p>
      {!tenantCtx && <>
        <label htmlFor="my-data-community">Community</label>
        <select id="my-data-community" value={selected?._id || ''} onChange={(event) => setSelectedId(event.target.value)}>
          <option value="">Select a community</option>
          {choices.map((choice) => <option key={choice._id} value={choice._id}>{tenantName(choice)}</option>)}
        </select>
        {loading && <p role="status">Loading communities...</p>}
        {error && <div role="alert"><p>{error}</p><button onClick={() => setRevision((value) => value + 1)}>Retry communities</button></div>}
      </>}
      {tenantCtx?.error ? <p role="alert">{tenantCtx.error}</p>
        : tenantCtx?.loading ? <p role="status">Loading community...</p>
          : tenant ? <MyHarvestData key={`${tenant._id}-${recordId}`} tenant={tenant}
            recordId={!requestedTenantId || requestedTenantId === String(tenant._id) ? recordId : ''} />
            : <p>Select a community to view your data. General Public data is kept separate from your other communities.</p>}
    </div>
  );
}
